// Angular imports
import { Component, Injector, OnInit, ViewChild } from '@angular/core';

// 3rd party imports
import { DxMapComponent } from 'devextreme-angular';

// Component imports
import { UserCommunitiesBaseComponent } from '../_base/user-communities-base.component';
import { UserCommunityMapPopoverComponent } from './user-community-map-popover/user-community-map-popover.component';

// Service imports
import { SharedWidgetsService } from '../../shared-widgets-module/_services/shared-widgets.service';
import { UserCommunitiesService } from '../_services/user-communities.service';
import { CommunityService } from '../../_services/data-services/community.service';

// Constant imports
import { Constants } from '../../_constants/constants';

// Model imports
import { UserCommunityCoordinatesResultDto } from '../_models/user-communities/user-community-coordinates-result-dto';

declare const google: any;

@Component({
    selector: 'app-user-communities-map',
    templateUrl: './user-communities-map.component.html',
    styleUrls: ['./user-communities-map.component.scss']
})
export class UserCommunitiesMapComponent extends UserCommunitiesBaseComponent implements OnInit {
    @ViewChild(UserCommunityMapPopoverComponent, { static: false }) popover: UserCommunityMapPopoverComponent;
    @ViewChild(DxMapComponent, { static: false }) map: DxMapComponent;

    mapKeys = Constants.mapKeys;
    communities: UserCommunityCoordinatesResultDto[];
    mapInstance: any;
    mapCenter = { lat: 30.266666, lng: -97.73333 };
    mapMarkers = {};
    mapVisible = false;
    isStreetView = false;
    mapMarkerInfo: any;
    selectedMarker: any;
    selectedCommunity: UserCommunityCoordinatesResultDto;

    constructor(
        protected readonly injector: Injector,
        protected readonly sharedWidgetService: SharedWidgetsService,
        protected readonly communitiesService: UserCommunitiesService,
        private readonly communityService: CommunityService
    ) {
        super(injector, sharedWidgetService, communitiesService);
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

    doRefresh(clearCache = false): void {
        this.filterParams.clearCache = clearCache;
        this.portfolioHelper.showLoading();
        this.communitiesService.getUserCommunitiesMap(this.filterParams).subscribe(
            (results) => {
                results.forEach((r) => {
                    r.communityInfo = this.communityService.getCommunityStatusInfo(r.communityId);
                });
                this.communities = results;
                this.updateMapVisibility();
                this.portfolioHelper.hideLoading();
            },
            (error) => {
                this.errorService.displayError(error);
                this.portfolioHelper.hideLoading();
            }
        );
    }

    onMapReady(e: any): void {
        this.mapInstance = e.originalMap;
        this.mapInstance.setOptions({
            gestureHandling: 'cooperative',
            styles: [
                {
                    featureType: 'all',
                    elementType: 'all',
                    stylers: [{ visibility: 'on' }]
                }
            ]
        });
        const streetView = this.mapInstance.getStreetView();
        streetView.addListener('visible_changed', () => {
            this.isStreetView = streetView.getVisible();
            this.updatePropertiesMarkers();
        });

        this.initMapPopoverComponent();
        this.addCommunitiesMarkers(e.originalMap);
        this.addPlacesSearchBox(e.originalMap);
    }

    private updateMapVisibility(): void {
        this.mapVisible = false;
        this.mapMarkerInfo = null;
        this.selectedCommunity = null;
        Object.keys(this.mapMarkers).forEach((key: string) => {
            this.mapMarkers[key].setMap(null);
        });
        this.mapMarkers = {};
        setTimeout(() => (this.mapVisible = true), 500);
    }

    private updatePropertiesMarkers(): void {
        const mapInstance = this.isStreetView ? null : this.mapInstance;
        Object.keys(this.mapMarkers).forEach((key: string) => {
            this.mapMarkers[key].setMap(mapInstance);
        });
    }

    private initMapPopoverComponent(): void {
        // Defining inner class here as google.maps package will be available only after onMapReady event
        class MapPopoverComponent extends google.maps.OverlayView {
            popover: UserCommunityMapPopoverComponent;
            container: HTMLDivElement;
            imageTarget: HTMLDivElement;
            marker: any;

            constructor(popover: UserCommunityMapPopoverComponent) {
                super();
                this.popover = popover;
                this.popover.onUpdate.subscribe(() => {
                    setTimeout(this.draw.bind(this), 10);
                });
                // Create a container for marker
                this.container = document.createElement('div');
                this.container.style.position = 'absolute';
                this.container.style.zIndex = '1000';
                // Create image target for popover
                this.imageTarget = document.createElement('div');
                this.imageTarget.style.width = '1px';
                this.imageTarget.style.height = '1px';
                this.container.appendChild(this.imageTarget);
                // Stop clicks, etc., from bubbling up to the map.
                MapPopoverComponent.preventMapHitsAndGesturesFrom(this.container);
            }

            setMarker(marker) {
                if (!!this.marker) {
                    this.resetMarkerIcon(this.marker);
                    this.setMap(null);
                }
                setTimeout(() => {
                    if (!!marker) {
                        this.marker = marker;
                        this.updateMarkerIcon(marker);
                        if (this.popover.isStreetView) {
                            this.setMap(marker.getMap().getStreetView());
                        } else {
                            this.setMap(marker.getMap());
                        }
                    }
                }, 10);
            }

            /** Called when the popup is added to the map. */
            onAdd() {
                this.getPanes().floatPane.appendChild(this.container);
                this.popover.option('container', this.container);
                this.popover.show(this.imageTarget);
            }

            /** Called when the popup is removed from the map. */
            onRemove() {
                this.popover.hide();
                this.popover.option('container', undefined);
                if (this.container.parentElement) {
                    this.container.parentElement.removeChild(this.container);
                }
            }

            /** Called each frame when the popup needs to draw itself. */
            draw() {
                const position = this.getProjection().fromLatLngToDivPixel(this.marker.getPosition());
                // Hide the popup when it is far out of view.
                const display = Math.abs(position.x) < 4000 && Math.abs(position.y) < 4000 ? 'block' : 'none';
                if (display === 'block') {
                    this.container.style.left = position.x + 'px';
                    this.container.style.top = position.y + 'px';
                }
                if (this.container.style.display !== display) {
                    this.container.style.display = display;
                }
                this.popover.repaint();
            }

            private updateMarkerIcon(marker) {
                const imageUrl = marker.getIcon().url;
                const tokens = imageUrl.split('/');
                const imageName = tokens[tokens.length - 1];
                tokens[tokens.length - 1] = `selected-${imageName}`;
                marker.setIcon({
                    url: tokens.join('/'),
                    scaledSize: new google.maps.Size(32, 48)
                });
                const zIndex = marker.getZIndex();
                marker.setZIndex(zIndex + 10000);
            }

            private resetMarkerIcon(marker) {
                const imageUrl = marker.getIcon().url;
                const tokens = imageUrl.split('/');
                const imageName = tokens[tokens.length - 1];
                tokens[tokens.length - 1] = imageName.replace('selected-', '');
                marker.setIcon({
                    url: tokens.join('/'),
                    scaledSize: new google.maps.Size(32, 48)
                });
                const zIndex = marker.getZIndex();
                marker.setZIndex(zIndex - 10000);
            }
        }

        this.mapMarkerInfo = new MapPopoverComponent(this.popover);
        this.popover.onUpdate.subscribe((flag: boolean) => {
            this.mapInstance.panTo(this.mapMarkerInfo.marker.getPosition());
        });
    }

    private addCommunitiesMarkers(map: any): void {
        this.mapMarkers = {};
        const bounds = new google.maps.LatLngBounds();
        this.communities.forEach((record) => {
            bounds.extend(new google.maps.LatLng(record.latitude, record.longitude));
            const key = `${record.communityId}_${record.communityName}`;
            let markerColor = 'green';
            if (!record.communityInfo) {
                markerColor = 'yellow';
            } else if (!record.communityInfo.isActive) {
                markerColor = 'red';
            }
            const marker = this.addMapMarker(record, markerColor);
            this.mapMarkers[key] = marker;
        });
        map.setCenter(bounds.getCenter());
        map.fitBounds(bounds);
    }

    private addPlacesSearchBox(map: any): void {
        // Create the search box and link it to the UI element.
        const input = document.getElementById('places-search') as HTMLInputElement;
        map.controls[google.maps.ControlPosition.TOP_RIGHT].push(input);
        // Bias the SearchBox results towards current map's viewport.
        const searchBox = new google.maps.places.SearchBox(input);
        map.addListener('bounds_changed', () => {
            searchBox.setBounds(map.getBounds());
        });

        let placesMarkers = [];
        searchBox.addListener('places_changed', () => {
            // Clear out the old markers.
            placesMarkers.forEach((marker) => {
                marker.setMap(null);
            });
            placesMarkers = [];

            const places = searchBox.getPlaces();
            if (places.length === 0) {
                return;
            }

            // For each place, get the icon, name and location.
            const bounds = new google.maps.LatLngBounds();
            places.forEach((place) => {
                if (!place.geometry || !place.geometry.location) {
                    return;
                }
                const icon = {
                    url: place.icon as string,
                    size: new google.maps.Size(71, 71),
                    origin: new google.maps.Point(0, 0),
                    anchor: new google.maps.Point(17, 34),
                    scaledSize: new google.maps.Size(25, 25)
                };

                // Create a marker for each place.
                placesMarkers.push(
                    new google.maps.Marker({
                        map,
                        icon,
                        title: place.name,
                        position: place.geometry.location
                    })
                );
                if (place.geometry.viewport) {
                    // Only geocodes have viewport.
                    bounds.union(place.geometry.viewport);
                } else {
                    bounds.extend(place.geometry.location);
                }
            });
            map.fitBounds(bounds);
        });
    }

    private addMapMarker(record: UserCommunityCoordinatesResultDto, markerColor = 'green', labelText = '0'): any {
        const marker = new google.maps.Marker({
            optimized: true,
            map: this.mapInstance,
            position: new google.maps.LatLng(record.latitude, record.longitude),
            icon: this.getMarkerIcon(markerColor, labelText),
            title: `${record.communityName}`
        });
        marker.addListener('click', () => {
            this.setMapMarker(record, marker);
        });
        return marker;
    }

    private setMapMarker(record: UserCommunityCoordinatesResultDto, marker: any): void {
        this.selectedCommunity = record;
        this.selectedMarker = marker;
        this.mapInstance.panTo(marker.getPosition());
        if (!!this.mapMarkerInfo) {
            this.mapMarkerInfo.setMarker(marker);
        }
    }

    private getMarkerIcon(markerColor = 'green', markerText = '0'): any {
        return {
            url: `assets/images/markers/${markerColor}/${markerText}.png`,
            scaledSize: new google.maps.Size(32, 48)
        };
    }
}
